import React, { useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { KENO_API } from '../keno/fairnessApi';
import { useOriginalsAccess } from './originalsAccessContext';
import { formatApiErrorDetail } from '../../auth';

const randomSeed = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) => byte.toString(16).padStart(2, '0')).join('');

export const AdminSeedRotation = ({ game, userId, preview, onRotated, onRefresh }) => {
  const access = useOriginalsAccess();
  const [open, setOpen] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [clientSeed, setClientSeed] = useState(randomSeed);
  const rotate = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${KENO_API}/admin/games/${game}/fairness/rotate`, {
        seed_id: preview.seed_id, nonce: preview.nonce, client_seed: clientSeed,
        next_server_seed_hash: preview.next_server_seed_hash,
      }, { headers: access.headers, params: { user_id: userId } });
      if (data.current.server_seed_hash !== preview.next_server_seed_hash) throw new Error('The activated seed did not match its commitment.');
      setOpen(false); await onRotated(data);
    } catch (failure) {
      access.handleFailure(failure);
      setError(formatApiErrorDetail(failure.response?.data?.detail || failure.message || 'Unable to rotate this seed pair.'));
      if (failure.response?.status === 409) await onRefresh();
    } finally { setBusy(false); }
  };
  return <div className="admin-seed-rotation" data-testid="admin-seed-rotation">
    <h4>Rotate Seed Pair</h4>
    <label htmlFor="admin-new-client-seed">New Client Seed</label>
    <div className="admin-seed-change"><Input id="admin-new-client-seed" data-testid="admin-new-client-seed" value={clientSeed} maxLength={64} disabled={preview.rotation_locked || busy} onChange={(event) => setClientSeed(event.target.value)} />
      <Button data-testid="admin-seed-rotate-open" disabled={preview.rotation_locked || busy || !preview.next_server_seed_hash || !/^[A-Za-z0-9_-]{1,64}$/.test(clientSeed)} onClick={() => { setError(''); setOpen(true); }}><RefreshCw size={16} />Change</Button></div>
    {preview.rotation_locked && <p role="status" data-testid="admin-seed-locked">{preview.lock_reason}</p>}
    {!open && error && <p role="alert" className="keno-error" data-testid="admin-seed-rotation-error">{error}</p>}
    <Dialog open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }}><DialogContent className="admin-seed-confirm" data-testid="admin-seed-confirm-dialog">
      <DialogHeader><DialogTitle>Rotate {preview.username}’s seed pair?</DialogTitle><DialogDescription>This changes the shared pair for all five Originals, not just {game}. Previous bets and balances stay unchanged.</DialogDescription></DialogHeader>
      <dl><dt>New client seed</dt><dd data-testid="admin-seed-confirm-client">{clientSeed}</dd><dt>Server commitment to activate</dt><dd data-testid="admin-seed-confirm-hash">{preview.next_server_seed_hash}</dd></dl>
      {error && <p role="alert" className="keno-error" data-testid="admin-seed-confirm-error">{error}</p>}
      <div className="admin-seed-confirm-actions"><Button variant="outline" data-testid="admin-seed-rotate-cancel" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="admin-seed-rotate-confirm" disabled={busy} onClick={rotate}>{busy ? 'Changing…' : 'Rotate seed pair'}</Button></div>
    </DialogContent></Dialog>
  </div>;
};